import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ListChecks, ShieldAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { StateBadge } from "@/components/platform/Badges";
import { AuditTimeline } from "@/components/platform/AuditTimeline";
import { ApprovalPanel } from "@/components/platform/ApprovalPanel";
import { Panel } from "@/components/rights/RightsBadges";
import { ContractBadge, RiskBadge } from "@/components/vendors/VendorBadges";
import { RiskPanel } from "@/components/vendors/RiskPanel";
import { RopaLinksPanel } from "@/components/vendors/RopaLinksPanel";
import { AssessmentsPanel } from "@/components/vendors/AssessmentsPanel";
import { ContractPanel } from "@/components/vendors/ContractPanel";
import { ThirdPartiesPanel } from "@/components/vendors/ThirdPartiesPanel";
import { VendorEvidencePanel } from "@/components/vendors/VendorEvidencePanel";
import { VendorDetails } from "@/components/vendors/VendorDetails";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useDirectory } from "@/lib/rights";
import { useVendorMeta } from "@/lib/vendors";

const TaskList = ({ tasks }) => (
  <Panel eyebrow="Task engine" title={`Linked tasks (${tasks.length})`} testId="vendor-tasks">
    {tasks.length === 0 ? <div className="text-xs text-slate-500">Raise tasks from assessment gaps or contract controls.</div> : (
      <ul className="space-y-2">
        {tasks.map((t) => (
          <li key={t.id} className="flex items-center gap-2 text-xs" data-testid={`vendor-task-${t.id}`}>
            <ListChecks className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to={`/tasks?task=${t.id}`} className="flex-1 min-w-0 truncate text-[#008080] hover:underline">{t.task_ref} · {t.title}</Link>
            <StateBadge state={t.status} />
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

const IncidentsList = ({ incidents }) => (
  <Panel eyebrow="Breach Desk" title={`Linked incidents (${incidents.length})`} testId="vendor-incidents">
    {incidents.length === 0 ? <div className="text-xs text-slate-500">No incidents linked to this vendor.</div> : (
      <ul className="space-y-2">
        {incidents.map((i) => (
          <li key={i.id} className="flex items-center gap-2 text-xs" data-testid={`vendor-incident-${i.id}`}>
            <ShieldAlert className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <Link to={`/breach/${i.id}`} className="flex-1 min-w-0 truncate text-[#008080] hover:underline">{i.incident_reference} · {i.incident_title}</Link>
            <StateBadge state={i.status} />
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

export default function VendorProfile() {
  const { vid } = useParams();
  const auth = useAuth();
  const meta = useVendorMeta();
  const users = useDirectory();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const canEdit = auth.can("vendors", "edit");

  const load = useCallback(async () => {
    try { setV((await api.get(`/vendors/${vid}`)).data); } catch (e) { setErr(apiError(e, "Could not load vendor")); }
  }, [vid]);
  useEffect(() => { load(); }, [load]);

  if (err) return <AppShell testId="vendor-profile-page"><div className="text-sm text-red-600" data-testid="vendor-profile-error">{err}</div></AppShell>;
  if (!v || !meta) return <AppShell testId="vendor-profile-page"><div className="h-40 rounded-xl bg-slate-100 animate-pulse" /></AppShell>;
  const pending = v.approvals.find((a) => a.status === "Pending");

  return (
    <AppShell testId="vendor-profile-page">
      <Link to="/vendors?view=inventory" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#003366]" data-testid="vendor-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Vendor inventory
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-4 rr-rise">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-accent-sm" data-testid="vendor-profile-ref">{v.vendor_id}</span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600" data-testid="vendor-profile-status">{v.status_label}</span>
            <ContractBadge status={v.contract_status} label={`Contract ${v.contract_status_label.toLowerCase()}`} />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003366] mt-1.5" data-testid="vendor-profile-name">{v.name}</h1>
          <p className="text-sm text-slate-600 mt-1">{v.service_type} · {v.country} · owner {v.owner}</p>
        </div>
        <div className="flex items-center gap-2">
          <RiskBadge level={v.inherent_risk_rating} prefix="Inherent" testId="vendor-profile-inherent" />
          <RiskBadge level={v.risk_level} prefix="Final" testId="vendor-profile-final" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="vendor-profile-tabs">
          <TabsTrigger value="overview" data-testid="vtab-overview">Overview & risk</TabsTrigger>
          <TabsTrigger value="ropa" data-testid="vtab-ropa">RoPA links</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="vtab-assessments">Assessments ({v.reviews.length})</TabsTrigger>
          <TabsTrigger value="contract" data-testid="vtab-contract">Contract controls</TabsTrigger>
          <TabsTrigger value="parties" data-testid="vtab-parties">Sub-processors & contacts</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="vtab-evidence">Evidence ({v.evidence.length})</TabsTrigger>
          <TabsTrigger value="activity" data-testid="vtab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-5">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <div className="xl:col-span-8"><RiskPanel v={v} meta={meta} canEdit={canEdit} onChanged={load} /></div>
            <aside className="xl:col-span-4 space-y-6">
              {pending && <Panel eyebrow="Approval engine" title="Pending approval" testId="vendor-pending-approval">
                <ApprovalPanel approval={pending} currentUser={auth.user} onChanged={load} /></Panel>}
              <VendorDetails key={v.updated_at || JSON.stringify([v.name, v.status, v.owner])} v={v} meta={meta} users={users} canEdit={canEdit} onChanged={load} />
              <TaskList tasks={v.tasks} />
              <IncidentsList incidents={v.incidents || []} />
            </aside>
          </div>
        </TabsContent>
        <TabsContent value="ropa" className="mt-5"><RopaLinksPanel v={v} canEdit={canEdit} onChanged={load} /></TabsContent>
        <TabsContent value="assessments" className="mt-5"><AssessmentsPanel v={v} canEdit={canEdit} /></TabsContent>
        <TabsContent value="contract" className="mt-5"><ContractPanel v={v} meta={meta} canEdit={canEdit} onChanged={load} /></TabsContent>
        <TabsContent value="parties" className="mt-5"><ThirdPartiesPanel v={v} canEdit={canEdit} currentUser={auth.user?.username} onChanged={load} /></TabsContent>
        <TabsContent value="evidence" className="mt-5"><VendorEvidencePanel v={v} meta={meta} canEdit={canEdit} onChanged={load} /></TabsContent>
        <TabsContent value="activity" className="mt-5">
          <Panel eyebrow="Universal audit log" title="Activity" testId="vendor-activity"><AuditTimeline items={v.activity} testId="vendor-audit-timeline" /></Panel>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
